// Project/railways/RailwayPayment.jsx

import axios from "axios";
import React, { useRef, useState } from "react";
import { toast } from "react-toastify";
import { BASE_URL, AUTH } from "../../../config/axios";
import {
  getModalPayment,
  getDeductionBase,
  getBasisLabel,
  hasCostBreakup,
  DEDUCTION_BASIS_OPTIONS,
  DEFAULT_DEDUCTION_BASIS,
} from "../paymentUtils";

const RailwayPayment = ({
  type,
  stageData,
  totalStageCost,
  totalCompleted,
  i,
  documentData,
  getProject,
  project,
  allData,
  id,
  submission_id,
  status,
  submissionItem,
  getDocuments,
}) => {
  const modalRef = useRef(null);
  // Basic Value / GST / Total Amount of the project (comes with allData)
  const projectCost = allData?.project_details_data || {};
  const [show, setShow] = useState(false);
  const [localEditMode, setLocalEditMode] = useState(false);
  const [cost, setCost] = useState(0);
  const [aboutTobill, setaboutTobill] = useState(0);
  const [inputType, setInputType] = useState("null");

  const defaultPayment = {
    billingClaimedPercent: 0,   billingClaimedAmount: 0,
    billingReceivedPercent: 0,  billingReceivedAmount: 0,
    tdsPercent: 0,              tdsAmount: 0,              tdsBasis: DEFAULT_DEDUCTION_BASIS,
    retentionPercent: 0,        retentionAmount: 0,        retentionBasis: DEFAULT_DEDUCTION_BASIS,
    LabourcessPercent: 0,       LabourcessAmount: 0,       LabourcessBasis: DEFAULT_DEDUCTION_BASIS,
    otherDeductionPercent: 0,   otherDeductionAmount: 0,   otherDeductionBasis: DEFAULT_DEDUCTION_BASIS,
    totalDeductionAmount: 0,    netReceivedAmount: 0,
    balanceAmount: 0,           remarks: "",
  };

  const [localPayment, setLocalPayment] = useState(defaultPayment);

  const round = (val) => Number(Number(val || 0).toFixed(2));

  // ─── Open & prefill ───────────────────────────────────────────────────────
  const handleShow = () => {
    if (type === "Stage") {
      setCost(Number(i.cost || 0));
      setLocalPayment({ ...defaultPayment, ...getModalPayment(i) });
      setShow(true);
    } else if (type === "Input") {
      setCost(Number(i.cost || 0));
      setLocalPayment({ ...defaultPayment, ...getModalPayment(i) });
      const currentInputType = i?.normal_input?.[0]?.input_type || "null";
      setInputType(currentInputType);
      if (currentInputType === "Stage type") {
        const totalAboutToBill = i?.normal_input?.[0]?.stage?.reduce(
          (total, item) => total + Number(item.completed_work || 0), 0,
        ) || 0;
        setaboutTobill(totalAboutToBill);
      } else if (currentInputType === "Normal Input") {
        setaboutTobill(Number(i?.normal_input?.[0]?.about_to_bill || 0));
      } else {
        setaboutTobill(Number(i?.normal_input?.[0]?.cost || 0));
      }
      setShow(true);
    } else {
      setCost(Number(i.cost || 0));
      setLocalPayment({ ...defaultPayment, ...getModalPayment(i) });
      setShow(true);
    }
  };

  const handleClose = () => { setShow(false); setLocalEditMode(false); };

  const formatCurrencyIndian = (amount) => {
    if (amount === null || amount === undefined) return "-";
    const rounded = Number(amount).toFixed(2);
    return "₹" + new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 0, maximumFractionDigits: 2,
    }).format(rounded);
  };

  // ─── aboutToBill — stable, comes from props/state not localPayment ────────
  let aboutToBill = 0;
  if (type === "Input") {
    aboutToBill = (inputType === "Normal Input" || inputType === "Stage type")
      ? round(aboutTobill) : round(cost);
  } else if (type === "Stage") {
    aboutToBill = round(totalCompleted);
  } else {
    aboutToBill = (i?.submission_data || [])
      .filter(s => s.status === "completed")
      .reduce((acc, cur) => acc + Number(cur.cost || 0), 0);
  }

  // ─── All display values read from localPayment ────────────────────────────
  const billingClaimed   = round(localPayment.billingClaimedAmount);
  const billingReceived  = round(localPayment.billingReceivedAmount);
  const tds              = round(localPayment.tdsAmount);
  const retention        = round(localPayment.retentionAmount);
  const Labourcess       = round(localPayment.LabourcessAmount);
  const other            = round(localPayment.otherDeductionAmount);
  const totalDeduction   = round(tds + retention + Labourcess + other);
  const netReceived      = round(billingReceived - totalDeduction);
  const balanceToClaim   = round(aboutToBill - billingClaimed);
  const balanceToReceive = round(billingClaimed - billingReceived);
  const receivedAmount   = billingReceived;

  // ─── Deduction base = share of the approved amount that belongs to the ────
  // chosen basis (Basic Value / GST / Total Amount). Always read from `prev`
  // so it is never stale.
  const deductionBase = (prev, basisKey) =>
    round(
      getDeductionBase(
        round(prev.billingReceivedAmount),
        prev[basisKey] || DEFAULT_DEDUCTION_BASIS,
        projectCost,
      ),
    );

  // Same helper for the render (uses current localPayment)
  const baseNow = (basisKey) => deductionBase(localPayment, basisKey);

  // Base-value getters for each row:
  //   Claimed   -> aboutToBill (stable number)
  //   Approved  -> prev.billingClaimedAmount
  //   Deduction -> basis-aware share of prev.billingReceivedAmount
  const handlePctChange = (pctKey, amtKey, getBaseVal) => (e) => {
    const pct = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
    setLocalPayment(prev => {
      const baseVal = typeof getBaseVal === "function" ? getBaseVal(prev) : getBaseVal;
      const amt = round((baseVal * pct) / 100);
      return { ...prev, [pctKey]: pct, [amtKey]: amt };
    });
  };

  const handleAmtChange = (amtKey, pctKey, getBaseVal) => (e) => {
    const amt = Math.max(0, parseFloat(e.target.value) || 0);
    setLocalPayment(prev => {
      const baseVal = typeof getBaseVal === "function" ? getBaseVal(prev) : getBaseVal;
      const effectiveBase = baseVal > 0 ? baseVal : 1;
      const pct = parseFloat(Math.min(100, (amt / effectiveBase) * 100).toFixed(2));
      return { ...prev, [amtKey]: amt, [pctKey]: pct };
    });
  };

  // When the basis dropdown changes, keep the % and recalculate the ₹ amount
  const handleBasisChange = (basisKey, pctKey, amtKey) => (e) => {
    const newBasis = e.target.value;
    setLocalPayment(prev => {
      const base = round(
        getDeductionBase(round(prev.billingReceivedAmount), newBasis, projectCost),
      );
      const amt = round((base * Number(prev[pctKey] || 0)) / 100);
      return { ...prev, [basisKey]: newBasis, [amtKey]: amt };
    });
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const updatedPayment = {
    remarks: localPayment.remarks || "",
    billingClaimedPercent: localPayment.billingClaimedPercent,
    billingClaimedAmount: billingClaimed,
    billingReceivedPercent: localPayment.billingReceivedPercent,
    billingReceivedAmount: billingReceived,
    tdsPercent: localPayment.tdsPercent,
    tdsAmount: tds,
    tdsBasis: localPayment.tdsBasis || DEFAULT_DEDUCTION_BASIS,
    retentionPercent: localPayment.retentionPercent,
    retentionAmount: retention,
    retentionBasis: localPayment.retentionBasis || DEFAULT_DEDUCTION_BASIS,
    LabourcessPercent: localPayment.LabourcessPercent,
    LabourcessAmount: Labourcess,
    LabourcessBasis: localPayment.LabourcessBasis || DEFAULT_DEDUCTION_BASIS,
    otherDeductionPercent: localPayment.otherDeductionPercent,
    otherDeductionAmount: other,
    otherDeductionBasis: localPayment.otherDeductionBasis || DEFAULT_DEDUCTION_BASIS,
    totalDeductionAmount: totalDeduction,
    netReceivedAmount: netReceived,
    balanceAmount: balanceToReceive,
    aboutToBillAmount: aboutToBill,
    balanceToClaimAmount: balanceToClaim,
    balanceToReceiveAmount: balanceToReceive,
  };

  // Lines shown in the always-visible summary ("TDS 2% on Basic Value  −₹x")
  const deductionLines = [
    { label: "TDS",         pctKey: "tdsPercent",            basisKey: "tdsBasis",            amt: tds },
    { label: "Retention",   pctKey: "retentionPercent",      basisKey: "retentionBasis",      amt: retention },
    { label: "Labour Cess", pctKey: "LabourcessPercent",     basisKey: "LabourcessBasis",     amt: Labourcess },
    { label: "Other",       pctKey: "otherDeductionPercent", basisKey: "otherDeductionBasis", amt: other },
  ].filter((d) => d.amt > 0 || Number(localPayment[d.pctKey]) > 0);

  const handleSave = async () => {
    const loadingToastId = toast.loading("Updating payment... Please wait...");
    try {
      const updatedData = { ...allData };
      if (updatedData?.workstat && Array.isArray(updatedData.workstat)) {
        const targetWorkstat = updatedData.workstat.find((w) => w.id === i.id);
        if (targetWorkstat) {
          targetWorkstat.paymentData = {
            ...(targetWorkstat.paymentData || {}),
            ...updatedPayment,
          };
        }
      }
      const res = await axios.put(
        `${BASE_URL}/dpr/project/${project}/`,
        { workstat: updatedData.workstat },
        AUTH(),
      );
      if (res.status === 200 || res.status === 201) {
        toast.success("Payment data updated successfully!");
        setLocalEditMode(false);
        setShow(false);
        getProject();
      } else {
        toast.error("Update failed, please try again.");
      }
    } catch (err) {
      console.error("Error updating payment:", err);
      toast.error("Something went wrong while updating payment");
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  // ─── Drag ─────────────────────────────────────────────────────────────────
  const handleDrag = (e) => {
    const modal = modalRef.current;
    let shiftX = e.clientX - modal.getBoundingClientRect().left;
    let shiftY = e.clientY - modal.getBoundingClientRect().top;
    modal.style.position = "absolute";
    const onMouseMove = (e) => {
      modal.style.left = e.clientX - shiftX + "px";
      modal.style.top = e.clientY - shiftY + "px";
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup",
      () => document.removeEventListener("mousemove", onMouseMove),
      { once: true },
    );
  };

  // ─── UI helpers ───────────────────────────────────────────────────────────
  const kpiCard = (label, value, color, noBorderRight = false) => (
    <div style={{
      padding: "12px 14px",
      borderRight: noBorderRight ? "none" : "0.5px solid #e5e7eb",
      borderBottom: "0.5px solid #e5e7eb",
    }}>
      <div style={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.05em", color: "#6b7280", marginBottom: "5px", lineHeight: 1.3 }}>
        {label}
      </div>
      <div style={{ fontSize: "14px", fontWeight: 500, color }}>{value}</div>
    </div>
  );

  const chip = (label, bg, color) => (
    <span style={{ display: "inline-block", fontSize: "10px", fontWeight: 500, padding: "3px 9px", borderRadius: "20px", background: bg, color, whiteSpace: "nowrap" }}>
      {label}
    </span>
  );

  // ─── dualInput — reads pct and amt directly from localPayment ─────────────
  // getBaseVal is either a plain number (aboutToBill is stable)
  // or a function (prev) => value for anything derived from localPayment
  const dualInput = (pctKey, amtKey, getBaseVal) => (
    <div style={{ display: "flex", flexDirection: "column", gap: "4px", alignItems: "center" }}>
      {/* % input */}
      <div style={{ display: "flex", alignItems: "center", gap: "0" }}>
        <input
          type="number"
          value={localPayment[pctKey] === 0 ? "" : localPayment[pctKey]}
          min={0} max={100} step={0.01}
          placeholder="0"
          onFocus={(e) => e.target.select()}
          onChange={handlePctChange(pctKey, amtKey, getBaseVal)}
          style={{
            width: "48px",
            border: "0.5px solid #d1d5db",
            borderRadius: "6px 0 0 6px",
            padding: "3px 4px",
            fontSize: "12px",
            textAlign: "center",
            background: "#fff",
            color: "#111",
            outline: "none",
          }}
        />
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "0 6px", fontSize: "11px", fontWeight: 500, color: "#6b7280",
          background: "#f3f4f6", border: "0.5px solid #d1d5db", borderLeft: "none",
          borderRadius: "0 6px 6px 0", height: "26px",
        }}>%</span>
      </div>
      {/* ₹ amount input */}
      <div style={{ display: "flex", alignItems: "center", gap: "0" }}>
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          padding: "0 6px", fontSize: "11px", fontWeight: 500, color: "#6b7280",
          background: "#f3f4f6", border: "0.5px solid #d1d5db", borderRight: "none",
          borderRadius: "6px 0 0 6px", height: "26px",
        }}>₹</span>
        <input
          type="number"
          value={localPayment[amtKey] === 0 ? "" : localPayment[amtKey]}
          min={0}
          placeholder="0"
          onFocus={(e) => e.target.select()}
          onChange={handleAmtChange(amtKey, pctKey, getBaseVal)}
          style={{
            width: "80px",
            border: "0.5px solid #d1d5db",
            borderLeft: "none",
            borderRadius: "0 6px 6px 0",
            padding: "3px 4px",
            fontSize: "11px",
            textAlign: "center",
            background: "#f9fafb",
            color: "#374151",
            outline: "none",
          }}
        />
      </div>
    </div>
  );

  // Dropdown: calculate this deduction on Basic Value / GST / Total Amount
  const basisSelect = (basisKey, pctKey, amtKey) => (
    <div style={{ marginTop: 4 }}>
      <select
        value={localPayment[basisKey] || DEFAULT_DEDUCTION_BASIS}
        onChange={handleBasisChange(basisKey, pctKey, amtKey)}
        style={{
          fontSize: "11px",
          border: "0.5px solid #d1d5db",
          borderRadius: 6,
          padding: "2px 4px",
          background: "#fff",
          color: "#374151",
          maxWidth: "100%",
          cursor: "pointer",
        }}
      >
        {DEDUCTION_BASIS_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>On {o.label}</option>
        ))}
      </select>
      <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: 2 }}>
        Base: {formatCurrencyIndian(baseNow(basisKey))}
      </div>
    </div>
  );

  const groupRow = (label) => (
    <tr style={{ background: "#f9fafb" }}>
      <td colSpan={4} style={{ padding: "5px 8px", fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af" }}>
        {label}
      </td>
    </tr>
  );

  const tableRow = (dotColor, name, desc, pctKey, amtKey, getBaseVal, chipLabel, chipBg, chipColor, basisKey) => (
    <tr style={{ borderBottom: "0.5px solid #e5e7eb" }}>
      <td style={{ padding: "9px 8px" }}>
        <div style={{ display: "flex", gap: "8px", alignItems: "flex-start" }}>
          <div style={{ width: 7, height: 7, borderRadius: "50%", background: dotColor, marginTop: 3, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: "13px", fontWeight: 500, color: "#111" }}>{name}</div>
            <div style={{ fontSize: "11px", color: "#9ca3af", marginTop: 1 }}>{desc}</div>
            {basisKey && basisSelect(basisKey, pctKey, amtKey)}
          </div>
        </div>
      </td>
      <td style={{ padding: "9px 8px", textAlign: "center" }} colSpan={2}>
        {dualInput(pctKey, amtKey, getBaseVal)}
      </td>
      <td style={{ padding: "9px 8px", textAlign: "right" }}>
        {chip(chipLabel, chipBg, chipColor)}
      </td>
    </tr>
  );

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <style>{`
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
      `}</style>

      {/* Trigger button */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
        <button
          onClick={handleShow}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "5px",
            padding: "5px 12px",
            borderRadius: "5px",
            border: "0.5px solid #0F6E56",
            background: "#fff",
            color: "#0F6E56",
            fontSize: "12px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          ₹ Payment
        </button>
      </div>

      {show && (
        <div
          onClick={handleClose}
          style={{
            position: "fixed", top: 0, left: 0, width: "100%", height: "100%",
            background: "rgba(0,0,0,0.5)", backdropFilter: "blur(3px)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 999,
          }}
        >
          <div
            ref={modalRef}
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#fff", borderRadius: "20px",
              width: "min(560px, 94vw)", maxHeight: "90vh",
              overflowY: "auto", position: "relative",
              boxShadow: "0 20px 40px rgba(0,0,0,0.2)",
            }}
          >
            {/* ── HEADER ── */}
            <div
              onMouseDown={handleDrag}
              style={{
                position: "sticky", top: 0, zIndex: 10, background: "#7C3AED",
                padding: "18px 22px", display: "flex", alignItems: "center", gap: "14px",
                borderRadius: "20px 20px 0 0", cursor: "grab", userSelect: "none",
              }}
            >
              <div style={{ width: "38px", height: "38px", borderRadius: "10px", background: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                  <rect x="2" y="5" width="20" height="14" rx="2" />
                  <line x1="2" y1="10" x2="22" y2="10" />
                </svg>
              </div>
              <div style={{ flex: 1 }}>
                <h5 style={{ margin: 0, color: "#fff", fontWeight: 500, fontSize: "15px" }}>Payment details</h5>
                <p style={{ margin: 0, fontSize: "11px", color: "rgba(255,255,255,0.6)" }}>
                  {localEditMode ? "Edit mode · % ↔ ₹ linked" : "View mode · Railway billing"}
                </p>
              </div>
              {!localEditMode && (
                <button onClick={() => setLocalEditMode(true)} style={{ background: "rgba(255,255,255,0.15)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.3)", borderRadius: "20px", padding: "6px 16px", fontSize: "12px", fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                  Edit
                </button>
              )}
              {localEditMode && (
                <button onClick={handleSave} style={{ background: "rgba(255,255,255,0.25)", color: "#fff", border: "0.5px solid rgba(255,255,255,0.5)", borderRadius: "20px", padding: "6px 16px", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>
                  ✓ Save
                </button>
              )}
            </div>

            {/* ── KPI CARDS ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", borderBottom: "0.5px solid #e5e7eb" }}>
              {kpiCard("Stage cost",         formatCurrencyIndian(cost),            "#111827", false)}
              {kpiCard("About to bill",      formatCurrencyIndian(aboutToBill),     "#534AB7", false)}
              {kpiCard("Claimed",            formatCurrencyIndian(billingClaimed),  "#185FA5", false)}
              {kpiCard("Approved by Client", formatCurrencyIndian(billingReceived), "#27500A", true)}
              {kpiCard("Deduction",          formatCurrencyIndian(totalDeduction),  "#de2626", false)}
              {kpiCard("Bal. to claim",      formatCurrencyIndian(balanceToClaim),  "#185FA5", false)}
              {kpiCard("Bal. to receive",    formatCurrencyIndian(balanceToReceive),"#27500A", false)}
            </div>

            {/* ── EDIT TABLE ── */}
            {localEditMode && (
              <div style={{ padding: "14px 18px 0", animation: "slideDown 0.25s ease" }}>
                <div style={{ fontSize: "11px", color: "#7C3AED", marginBottom: "10px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span style={{ background: "#f5f3ff", border: "0.5px solid #7C3AED", borderRadius: "4px", padding: "1px 6px", fontSize: "10px" }}>tip</span>
                  Change % → ₹ updates · Change ₹ → % updates
                </div>
                {!hasCostBreakup(projectCost) && (
                  <div style={{ fontSize: "11px", color: "#92400e", background: "#fffbeb", border: "0.5px solid #fcd34d", borderRadius: "8px", padding: "7px 10px", marginBottom: "10px" }}>
                    Basic Value / GST is not entered for this project yet, so every option below uses the full amount. Add it in the project's Update form.
                  </div>
                )}
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", fontSize: "13px", border: "0.5px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" }}>
                  <colgroup>
                    <col style={{ width: "38%" }} />
                    <col style={{ width: "34%" }} />
                    <col style={{ width: "0%"  }} />
                    <col style={{ width: "28%" }} />
                  </colgroup>
                  <thead>
                    <tr style={{ borderBottom: "0.5px solid #e5e7eb", background: "#f9fafb" }}>
                      {[
                        { label: "Line item", align: "left"   },
                        { label: "% / ₹",     align: "center" },
                        { label: "",          align: "center" },
                        { label: "Status",    align: "right"  },
                      ].map((h, idx) => (
                        <th key={idx} style={{ fontSize: "10px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", padding: "10px 8px", textAlign: h.align }}>
                          {h.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groupRow("Billing")}

                    {/* ── aboutToBill is stable (from props/state) so pass directly ── */}
                    {tableRow("#7F77DD", "Claimed", "Of about-to-bill",
                      "billingClaimedPercent", "billingClaimedAmount",
                      aboutToBill,
                      "Billed", "#E6F1FB", "#185FA5"
                    )}

                    {/* ── baseVal = billingClaimedAmount — read fresh from prev ── */}
                    {tableRow("#7F77DD", "Approved by Client", "Of claimed amount",
                      "billingReceivedPercent", "billingReceivedAmount",
                      (prev) => round(prev.billingClaimedAmount),
                      "Received", "#EAF3DE", "#27500A"
                    )}

                    {groupRow("Deductions · applied on approved amount")}

                    {/* ── baseVal = share of billingReceivedAmount for the chosen basis ── */}
                    {tableRow("#E24B4A", "TDS", "Tax deducted at source",
                      "tdsPercent", "tdsAmount",
                      (prev) => deductionBase(prev, "tdsBasis"),
                      "Deducted", "#FCEBEB", "#791F1F", "tdsBasis"
                    )}
                    {tableRow("#E24B4A", "Retention", "Held until project close",
                      "retentionPercent", "retentionAmount",
                      (prev) => deductionBase(prev, "retentionBasis"),
                      "Deducted", "#FCEBEB", "#791F1F", "retentionBasis"
                    )}
                    {tableRow("#E24B4A", "Labour Cess", "Labour cess charge",
                      "LabourcessPercent", "LabourcessAmount",
                      (prev) => deductionBase(prev, "LabourcessBasis"),
                      "Deducted", "#FCEBEB", "#791F1F", "LabourcessBasis"
                    )}
                    {tableRow("#E24B4A", "Other", "Miscellaneous charges",
                      "otherDeductionPercent", "otherDeductionAmount",
                      (prev) => deductionBase(prev, "otherDeductionBasis"),
                      "Deducted", "#FCEBEB", "#791F1F", "otherDeductionBasis"
                    )}
                  </tbody>
                </table>
              </div>
            )}

            {/* ── SUMMARY BAND ── */}
            <div style={{ margin: "14px 18px 18px", border: "0.5px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" }}>
              {/* Which basis each deduction was calculated on */}
              {deductionLines.length > 0 && (
                <div style={{ padding: "10px 14px", borderBottom: "0.5px solid #e5e7eb" }}>
                  {deductionLines.map((d) => (
                    <div key={d.label} style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "#374151", padding: "2px 0" }}>
                      <span>
                        {d.label} {localPayment[d.pctKey] || 0}% on {getBasisLabel(localPayment[d.basisKey])}
                      </span>
                      <span style={{ color: "#791F1F", fontWeight: 500 }}>
                        −{formatCurrencyIndian(d.amt)}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 14px", background: "#f9fafb" }}>
                <div>
                  <div style={{ fontSize: "11px", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.06em", color: "#9ca3af", marginBottom: "3px" }}>
                    Net received
                  </div>
                  <div style={{ fontSize: "22px", fontWeight: 500, color: "#27500A" }}>
                    {formatCurrencyIndian(netReceived)}
                  </div>
                </div>
                <div style={{ fontSize: "11px", fontWeight: 500, padding: "4px 12px", borderRadius: "20px", background: "#fcebeb", color: "#b53232", border: "0.5px solid #dbb3b7" }}>
                  after −{formatCurrencyIndian(totalDeduction)} deductions
                </div>
              </div>
            </div>

            {/* ── REMARKS ── */}
            {localEditMode && (
              <div style={{ padding: "0 18px 14px" }}>
                <label style={{ fontSize: "11px", color: "#6b7280", fontWeight: 500, display: "block", marginBottom: "4px" }}>
                  Remarks
                </label>
                <textarea
                  value={localPayment.remarks || ""}
                  onChange={(e) => setLocalPayment((prev) => ({ ...prev, remarks: e.target.value }))}
                  rows={2}
                  style={{ width: "100%", border: "0.5px solid #d1d5db", borderRadius: "8px", padding: "8px 10px", fontSize: "13px", resize: "vertical", outline: "none" }}
                  placeholder="Add a remark..."
                />
              </div>
            )}

            {/* ── FOOTER ── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "8px", padding: "13px 18px", borderTop: "0.5px solid #e5e7eb" }}>
              {localEditMode && (
                <button onClick={() => setLocalEditMode(false)} style={{ background: "transparent", border: "0.5px solid #d1d5db", borderRadius: "20px", padding: "7px 18px", fontSize: "12px", color: "#9ca3af", cursor: "pointer" }}>
                  Cancel
                </button>
              )}
              <button onClick={handleClose} style={{ background: "transparent", border: "0.5px solid #d1d5db", borderRadius: "20px", padding: "7px 18px", fontSize: "12px", color: "#9ca3af", cursor: "pointer" }}>
                Close
              </button>
              {localEditMode && (
                <button onClick={handleSave} style={{ background: "#7C3AED", color: "#fff", border: "none", borderRadius: "20px", padding: "7px 22px", fontSize: "12px", fontWeight: 500, cursor: "pointer" }}>
                  Save changes
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { RailwayPayment };